import { CheckIcon, LoaderIcon } from "lucide-react";
import type { ReactElement } from "react";
import type { EnvironmentId } from "@cmmd-forge/contracts";

import { ProjectFavicon } from "../ProjectFavicon";
import { PROVIDER_SETTINGS_ICON_BY_PROVIDER } from "../providerSettingsIcons";
import {
  isSetupProjectOpenable,
  type ProviderSetupRow,
  type SetupStep,
  type SetupProjectRow,
} from "./firstAccountSetupData";

export function StepRow(props: {
  readonly step: SetupStep;
  readonly index: number;
  readonly activeIndex: number;
}): ReactElement {
  const complete = props.index < props.activeIndex;
  const active = props.index === props.activeIndex;
  const marker = complete ? <CheckIcon className="size-3" /> : props.index + 1;
  return (
    <li>
      <div
        className={[
          "relative grid grid-cols-[1.25rem_1fr] gap-2.5 rounded-md px-2 py-1.5 transition-colors",
          active
            ? "bg-muted/35 text-foreground before:absolute before:top-2 before:bottom-2 before:left-0 before:w-0.5 before:rounded-full before:bg-primary"
            : "text-muted-foreground",
        ].join(" ")}
      >
        <span
          className={[
            "flex size-5 items-center justify-center rounded-full border text-[10px] font-semibold tabular-nums",
            complete
              ? "border-success text-success-foreground"
              : active
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground",
          ].join(" ")}
        >
          {marker}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-xs font-semibold text-foreground">
            {props.step.label}
          </span>
        </span>
      </div>
    </li>
  );
}

function providerStatusDotClass(statusTone: ProviderSetupRow["statusTone"]): string {
  if (statusTone === "ready") {
    return "bg-success";
  }
  if (statusTone === "attention") {
    return "bg-warning";
  }
  return "bg-muted-foreground";
}

function ProviderMark(props: { readonly row: ProviderSetupRow }): ReactElement {
  const ProviderIcon = PROVIDER_SETTINGS_ICON_BY_PROVIDER[props.row.id];
  return (
    <span className="relative inline-flex size-5 shrink-0 items-center justify-center">
      {ProviderIcon ? (
        <ProviderIcon aria-hidden="true" className="size-4" />
      ) : (
        <span className="size-2 rounded-full bg-muted-foreground" />
      )}
      <span
        aria-hidden="true"
        className={[
          "pointer-events-none absolute -left-0.5 -top-0.5 size-2 rounded-full ring-2 ring-background",
          providerStatusDotClass(props.row.statusTone),
        ].join(" ")}
      />
    </span>
  );
}

function providerAccessViewModel(row: ProviderSetupRow): {
  readonly usesWorkspaceCredits: boolean;
  readonly usesPersonalSubscription: boolean;
  readonly statusLabel: string;
  readonly primaryActionLabel: string;
  readonly secondaryActionLabel: string;
  readonly canUsePersonal: boolean;
  readonly showCredentialSource: boolean;
  readonly showAction: boolean;
} {
  const showAction = row.authAction !== null;
  const usesWorkspaceCredits =
    row.credentialKind === "workspace" || row.credentialKind === "environment";
  const usesPersonalSubscription = row.credentialKind === "personal" && !showAction;
  const statusLabel = usesPersonalSubscription ? "Personal connected" : "Workspace access";
  return {
    usesWorkspaceCredits,
    usesPersonalSubscription,
    statusLabel,
    primaryActionLabel: usesWorkspaceCredits ? "Use project" : "Switch to project",
    secondaryActionLabel: usesPersonalSubscription ? "Change account" : "Connect personal",
    canUsePersonal: usesPersonalSubscription || showAction,
    showCredentialSource: row.credentialSource !== statusLabel,
    showAction,
  };
}

function ProviderCredentialSummary(props: {
  readonly row: ProviderSetupRow;
  readonly statusLabel: string;
  readonly showCredentialSource: boolean;
  readonly showAccountEmail: boolean;
}): ReactElement {
  const accountEmailAlreadyShown =
    props.showCredentialSource && props.row.accountEmail === props.row.credentialSource;
  return (
    <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <span>{props.statusLabel}</span>
      {props.showCredentialSource ? (
        <>
          <span className="text-muted-foreground/50">·</span>
          <span className="truncate">{props.row.credentialSource}</span>
        </>
      ) : null}
      {props.showAccountEmail && !accountEmailAlreadyShown ? (
        <>
          <span className="text-muted-foreground/50">·</span>
          <span className="truncate">{props.row.accountEmail}</span>
        </>
      ) : null}
    </span>
  );
}

function WorkspaceAccessButton(props: {
  readonly pressed: boolean;
  readonly label: string;
  readonly onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={props.pressed}
      className={[
        "inline-flex h-7 items-center whitespace-nowrap rounded-md border px-2.5 text-xs font-medium transition-colors",
        props.pressed
          ? "border-border bg-accent text-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
      ].join(" ")}
      onClick={props.onClick}
    >
      {props.label}
    </button>
  );
}

function ProviderConnectButton(props: {
  readonly row: ProviderSetupRow;
  readonly busy: boolean;
  readonly label: string;
  readonly onAction: (providerId: ProviderSetupRow["id"]) => void;
}): ReactElement {
  return (
    <button
      type="button"
      className="inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
      disabled={props.busy}
      onClick={() => props.onAction(props.row.id)}
    >
      {props.busy ? <LoaderIcon className="size-3 animate-spin" /> : null}
      {props.label}
    </button>
  );
}

function PersonalAccessButton(props: {
  readonly row: ProviderSetupRow;
  readonly pressed: boolean;
  readonly disabled: boolean;
  readonly busy: boolean;
  readonly label: string;
  readonly onAction: (providerId: ProviderSetupRow["id"]) => void;
}): ReactElement {
  return (
    <button
      type="button"
      aria-pressed={props.pressed}
      disabled={props.disabled}
      className={[
        "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-45",
        props.pressed
          ? "border-border bg-accent text-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
      ].join(" ")}
      onClick={() => props.onAction(props.row.id)}
    >
      {props.busy ? <LoaderIcon className="size-3 animate-spin" /> : null}
      {props.label}
    </button>
  );
}

function FixedProviderReadyBadge(): ReactElement {
  return (
    <span className="inline-flex h-7 items-center gap-1.5 rounded-md border border-success-border bg-success-surface px-2.5 text-xs font-medium text-foreground">
      <span className="size-1.5 rounded-full bg-success" />
      Ready
    </span>
  );
}

export function ProviderRow(props: {
  readonly row: ProviderSetupRow;
  readonly actionLabel: string | null;
  readonly actionBusy: boolean;
  readonly onUseWorkspaceCredits: () => void;
  readonly onAction: (providerId: ProviderSetupRow["id"]) => void;
}): ReactElement {
  const isFixedCmmdProvider = props.row.id === "apex";
  const access = providerAccessViewModel(props.row);
  const personalActionLabel = props.actionLabel ?? access.secondaryActionLabel;
  return (
    <tr className="border-t border-border">
      <td className="px-3 py-3.5">
        <span className="flex items-center gap-3">
          <ProviderMark row={props.row} />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground">{props.row.name}</span>
            {isFixedCmmdProvider ? null : (
              <ProviderCredentialSummary
                row={props.row}
                showAccountEmail={
                  access.usesPersonalSubscription && props.row.accountEmail !== null
                }
                showCredentialSource={access.showCredentialSource}
                statusLabel={access.statusLabel}
              />
            )}
          </span>
        </span>
      </td>
      <td className="px-3 py-3.5 text-right">
        {isFixedCmmdProvider ? (
          <FixedProviderReadyBadge />
        ) : (
          <div className="flex flex-wrap justify-end gap-2">
            <WorkspaceAccessButton
              label={access.primaryActionLabel}
              pressed={access.usesWorkspaceCredits}
              onClick={props.onUseWorkspaceCredits}
            />
            {access.showAction ? (
              <ProviderConnectButton
                busy={props.actionBusy}
                label={personalActionLabel}
                row={props.row}
                onAction={props.onAction}
              />
            ) : (
              <PersonalAccessButton
                busy={props.actionBusy}
                disabled={props.actionBusy || !access.canUsePersonal}
                label={personalActionLabel}
                pressed={access.usesPersonalSubscription}
                row={props.row}
                onAction={props.onAction}
              />
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function ProjectMark(props: {
  readonly project: SetupProjectRow;
  readonly label: string;
  readonly iconUrl: string | null | undefined;
}): ReactElement {
  if (props.project.source === "forge") {
    return (
      <ProjectFavicon
        environmentId={props.project.environmentId as EnvironmentId}
        cwd={props.project.cwd}
        projectName={props.project.name}
        className="mt-0.5 size-7 rounded-md"
      />
    );
  }

  const projectIcon = props.project.projectIcon?.trim();
  const projectIconUrl = props.project.projectIconUrl?.trim() || props.iconUrl;
  const fallback = props.label.trim().charAt(0).toUpperCase() || "W";
  return projectIconUrl ? (
    <img
      alt=""
      className="mt-0.5 size-7 shrink-0 rounded-md border border-border object-cover"
      src={projectIconUrl}
    />
  ) : projectIcon ? (
    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-[13px] font-semibold text-foreground">
      {projectIcon}
    </span>
  ) : (
    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-[11px] font-semibold text-muted-foreground">
      {fallback}
    </span>
  );
}

export function ProjectChoiceRow(props: {
  readonly project: SetupProjectRow;
  readonly selected: boolean;
  readonly onSelect: (projectId: string) => void;
  readonly onPrepareProject?: (project: SetupProjectRow) => void;
}): ReactElement {
  const openable = isSetupProjectOpenable(props.project);
  const availabilityLabel =
    props.project.source === "cmmd"
      ? props.project.githubRepoUrl
        ? "Needs import"
        : "Needs repo"
      : props.project.availability === "available"
        ? "Available"
        : props.project.availability === "missing"
          ? "Missing"
          : "Unknown";
  const detailLabel =
    props.project.source === "cmmd"
      ? (props.project.githubRepoUrl ?? props.project.cwd)
      : props.project.cwd;
  const sourceLabel =
    props.project.source === "cmmd"
      ? props.project.setupAction === "import"
        ? `${props.project.environmentLabel} · Import from GitHub`
        : `${props.project.environmentLabel} · Link a repository`
      : props.project.workspaceSummary
        ? `${props.project.workspaceSummary} · ${props.project.defaultProviderLabel}`
        : `${props.project.environmentLabel} · ${props.project.defaultProviderLabel}`;
  const setupActionLabel =
    props.project.setupAction === "import"
      ? "Import repo"
      : props.project.setupAction === "link-repo"
        ? "Link repo"
        : null;

  const content = (
    <span className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-3">
      <ProjectMark
        project={props.project}
        label={props.project.environmentLabel}
        iconUrl={props.project.environmentIconUrl}
      />
      <span className="grid min-w-0 gap-1">
        <span className="flex items-center justify-between gap-3">
          <span className="truncate text-sm font-semibold text-foreground">
            {props.project.name}
          </span>
          <span className="shrink-0 rounded-md border border-border bg-accent px-2 py-1 text-xs text-muted-foreground">
            {availabilityLabel}
          </span>
        </span>
        <span className="truncate text-xs text-muted-foreground">{detailLabel}</span>
        <span className="mt-1 flex min-w-0 items-center justify-between gap-3">
          <span className="truncate text-xs text-muted-foreground">{sourceLabel}</span>
          {setupActionLabel ? (
            <button
              type="button"
              className="inline-flex h-7 shrink-0 items-center rounded-md border border-border bg-background px-2.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!props.onPrepareProject}
              onClick={() => props.onPrepareProject?.(props.project)}
            >
              {setupActionLabel}
            </button>
          ) : null}
        </span>
      </span>
    </span>
  );

  return openable ? (
    <button
      type="button"
      aria-pressed={props.selected}
      onClick={() => props.onSelect(props.project.id)}
      className={[
        "grid gap-1 rounded-lg border p-4 text-left transition-colors",
        props.selected
          ? "border-primary bg-background"
          : "border-border bg-background/60 hover:border-border/80 hover:bg-accent/40",
      ].join(" ")}
    >
      {content}
    </button>
  ) : (
    <div className="grid gap-1 rounded-lg border border-border bg-muted/20 p-4 text-left">
      {content}
    </div>
  );
}
